import { Box, Button, Grid, styled, Typography } from '@mui/material';
import { AZURE_PORTAL_PATH, ExternalLink, TEST_IDS } from '@localstack/constants';
import { EmulatorType } from '@localstack/types';
import { LicenseService, buildRoute, isDarkMode, useApiGetter, useEndpoint, useSnackbar } from '@localstack/services';

import { AppRoute } from '~/config';

import { DeployIcon, GettingStartedIcon, HotReloadIcon, IntegrationsIcon, PipelineIcon, SamplesIcon } from './Icons';

type CommandTextProps = {
  isLoading?: boolean;
};

const CommandText = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isLoading',
})<CommandTextProps>(({ isLoading }) => ({
  flex: 1,
  minWidth: 0,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  opacity: isLoading ? 0.5 : 1,
}));

const CommandBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  fontFamily: 'monospace',
  fontSize: 12,
  padding: theme.spacing(1, 1, 1, 1.5),
  borderRadius: theme.spacing(1.5),
  backgroundColor: isDarkMode(theme) ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
}));

const CopyButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  padding: theme.spacing(0.75, 1.25),
  borderRadius: theme.spacing(1),
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1,
  letterSpacing: '0.04em',
  whiteSpace: 'nowrap',
}));

const PrimaryCopyButton = styled(CopyButton)(({ theme }) => ({
  backgroundColor: isDarkMode(theme) ? theme.palette.common.white : theme.palette.common.black,
  color: isDarkMode(theme) ? theme.palette.common.black : theme.palette.common.white,
  '&:hover': {
    backgroundColor: isDarkMode(theme) ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.88)',
  },
}));

const SecondaryCopyButton = styled(CopyButton)(({ theme }) => ({
  backgroundColor: isDarkMode(theme) ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: isDarkMode(theme) ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)',
  },
}));

const useCopyToClipboard = () => {
  const { showSnackbar } = useSnackbar();

  return (text: string, successMessage: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => showSnackbar({ message: successMessage, severity: 'success' }))
      .catch(() => showSnackbar({ message: 'Copy to clipboard failed', severity: 'error' }));
  };
};

type InstallSectionProps = {
  os: string;
};

export const InstallSection = ({ os }: InstallSectionProps): JSX.Element => {
  const isMac = os === 'macos';
  const installCommand = isMac ? 'brew install localstack/tap/lstk' : 'npm install -g @localstack/lstk';
  const copyToClipboard = useCopyToClipboard();

  return (
    <Box>
      <CommandBox>
        <CommandText>{installCommand}</CommandText>
        <PrimaryCopyButton onClick={() => copyToClipboard(installCommand, 'Command copied to clipboard!')}>
          COPY COMMAND
        </PrimaryCopyButton>
      </CommandBox>
    </Box>
  );
};

export const AuthTokenSection = (): JSX.Element => {
  const { data: authToken, isLoading } = useApiGetter(LicenseService, 'getWorkspaceToken', []);
  const token = authToken?.token ?? '';
  const maskedToken = token ? `${token.slice(0, 12)}****-****-****-${'*'.repeat(12)}` : '';
  const copyToClipboard = useCopyToClipboard();

  return (
    <>
      <CommandBox>
        <CommandText isLoading={isLoading}>{isLoading ? 'Loading...' : maskedToken}</CommandText>
        <SecondaryCopyButton onClick={() => copyToClipboard(token, 'Auth token copied to clipboard!')}>
          COPY TOKEN
        </SecondaryCopyButton>
      </CommandBox>
      <Typography fontSize={12} color="text.tertiary" mt={0.5}>
        Activate license with the{' '}
        <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 'inherit' }}>
          LOCALSTACK_AUTH_TOKEN
        </Box>{' '}
        environment variable in CLI, Docker, or Docker Compose.
      </Typography>
    </>
  );
};

interface DocTile {
  title: string;
  description: string;
  href: string;
  internal?: boolean;
  icon: JSX.Element;
}

const awsDocTiles: DocTile[] = [
  {
    title: 'Deploy resources',
    description: 'Manage resources with CLI, Terraform, or CloudFormation.',
    href: buildRoute(AppRoute.RESOURCES_OVERVIEW, { iid: 'default' }),
    internal: true,
    icon: <DeployIcon />,
  },
  {
    title: 'Build and test in pipelines',
    description: 'Infrastructure deployment on every push without cost.',
    href: ExternalLink.DOCS_CI,
    icon: <PipelineIcon />,
  },
  {
    title: 'Hot reload serverless functions',
    description: 'Redeploy changes instantly on save and debug functions locally.',
    href: ExternalLink.DOCS_LAMBDA_HOT_RELOADING,
    icon: <HotReloadIcon />,
  },
  {
    title: 'Sample Apps',
    description: 'Explore ready-to-run example applications and quickstart templates.',
    href: ExternalLink.DOCS_SAMPLE_APPS,
    icon: <SamplesIcon />,
  },
];

const snowflakeDocTiles: DocTile[] = [
  {
    title: 'Getting Started',
    description: 'Introduction to LocalStack for Snowflake',
    href: ExternalLink.SNOWFLAKE_INTRODUCTION,
    icon: <GettingStartedIcon />,
  },
  {
    title: 'Deploy resources',
    description: 'Manage resources with our Snowflake UI',
    href: buildRoute(AppRoute.INSTANCE_SNOWFLAKE, { iid: 'default' }),
    internal: true,
    icon: <DeployIcon />,
  },
  {
    title: 'CI/CD Integration',
    description: 'Run tests in your CI pipeline',
    href: ExternalLink.SNOWFLAKE_DOCS_CI,
    icon: <PipelineIcon />,
  },
  {
    title: 'Sample Apps',
    description: 'Explore example applications',
    href: ExternalLink.SNOWFLAKE_SAMPLE_REPO,
    icon: <SamplesIcon />,
  },
];

const azureDocTiles = (portalUrl: string): DocTile[] => [
  {
    title: 'Getting Started',
    description: 'Quickstart with LocalStack for Azure',
    href: ExternalLink.AZURE_GETTING_STARTED,
    icon: <GettingStartedIcon />,
  },
  {
    title: 'Azure Portal',
    description: 'Manage resources from the emulated Azure Portal.',
    href: portalUrl,
    icon: <DeployIcon />,
  },
  {
    title: 'Azure Integrations',
    description: 'Integrate with tools and frameworks',
    href: ExternalLink.AZURE_INTEGRATIONS,
    icon: <IntegrationsIcon />,
  },
  {
    title: 'Sample Apps',
    description: 'Explore sample applications',
    href: ExternalLink.AZURE_SAMPLES_REPO,
    icon: <SamplesIcon />,
  },
];

const getDocTiles = (portalUrl: string): Record<EmulatorType, DocTile[]> => ({
  [EmulatorType.AWS]: awsDocTiles,
  [EmulatorType.SNOWFLAKE]: snowflakeDocTiles,
  [EmulatorType.AZURE]: azureDocTiles(portalUrl),
});

const TITLE_LINE_HEIGHT = '20px';

const DocTileCardRoot = styled(Box)(({ theme }) => ({
  display: 'block',
  height: '100%',
  width: '100%',
  padding: theme.spacing(1.25),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  textDecoration: 'none',
  color: 'inherit',
  transition: 'background-color 200ms ease',
  '&:hover': { backgroundColor: theme.palette.action.hover },
})) as typeof Box;

const DocTileRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.25),
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.25),
}));

const DocTileIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  height: TITLE_LINE_HEIGHT,
  color: theme.palette.text.tertiary,
}));

const DocTileCard = ({ title, description, href, internal, icon }: DocTile): JSX.Element => (
  <DocTileCardRoot component="a" href={href} {...(!internal && { target: '_blank', rel: 'noopener noreferrer' })}>
    <DocTileRow>
      <DocTileIcon>{icon}</DocTileIcon>
      <Box>
        <Typography fontWeight={600} fontSize={13} lineHeight={TITLE_LINE_HEIGHT}>
          {title}
        </Typography>
        <Typography fontSize={12} color="text.secondary" lineHeight={1.3}>
          {description}
        </Typography>
      </Box>
    </DocTileRow>
  </DocTileCardRoot>
);

const DocTilesHeadingRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
}));

type DocTilesGridProps = {
  emulator: EmulatorType;
  emulatorToggleElement?: JSX.Element;
  /** Rendered instead of the tiles when the workspace is not entitled to this product. */
  prompt?: JSX.Element;
};

export const DocTilesGrid = ({ emulator, emulatorToggleElement, prompt }: DocTilesGridProps): JSX.Element => {
  const { endpoint } = useEndpoint();
  const tiles = getDocTiles(`${endpoint}${AZURE_PORTAL_PATH}`)[emulator];

  return (
    <>
      <DocTilesHeadingRow>
        <Typography component="h2" fontWeight={600} fontSize={14} color="text.primary">
          What to try next?
        </Typography>
        {emulatorToggleElement}
      </DocTilesHeadingRow>
      {prompt ?? (
        <Grid container spacing={1.5} data-testid={TEST_IDS.GETTING_STARTED_DOC_TILES(emulator)}>
          {tiles.map((tile) => (
            <Grid item xs={12} sm={6} key={tile.title}>
              <DocTileCard {...tile} />
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
};
